import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Link,
  Slide,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OnboardingDialogsContext } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import {
  ENTER_DELAY,
  EXIT_DELAY,
  SlideAnimationContext,
} from "contexts/SlideAnimation";
import { useContext, useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Map } from "schemas/functions";
import { routes } from "schemas/routes";

import AboutForm from "features/Auth/Onboarding/AboutForm";
import CategoryForm from "features/Auth/Onboarding/CategoryForm";
import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";
import { getSource, trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";

const styles = {
  formContainer: {
    p: { xs: 3, sm: 5, md: 6 },
  },
  closeIcon: {
    color: "primary.main",
    fontSize: 14,
    position: "absolute",
    right: 10,
    top: 10,
  },
  noOverflow: {
    overflowX: "hidden",
  },
  fullWidth: {
    width: "100%",
  },
};

enum Page {
  about = "about",
  categories = "categories",
  complete = "complete",
}

const ProfileDialog = () => {
  const theme = useTheme();
  const location = useLocation();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { templates } = useContext(OutreachTemplatesContext);
  const { setAlert } = useContext(AlertContext);
  const { getToken } = useAuth();
  const { currentUser, profile, setProfile, currentOrg } = useContext(
    OrganizationUserContext,
  );
  const {
    setProfileDialogOpen,
    setEmailBrandDialogOpen,
    setCreateTemplateOnboardingDialogOpen,
    noTemplates,
  } = useContext(OnboardingDialogsContext);
  const { slideOutLeft, slideInLeft, slideOutRight, slideInRight, slideProps } =
    useContext(SlideAnimationContext);
  const [page, setPage] = useState<Page>(Page.about);
  const source = getSource();

  const [onboardingForm, setOnboardingForm] = useState<Map>({});

  const saveOnboardingForm = async (onboardingForm: Map) => {
    const onboardingFormReq = {
      ...onboardingForm,
      signature: null, // do not overwrite default signature so that it updates using social links
      source,
      page,
    };

    try {
      const { organizationProfile } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding`,
        "PUT",
        {},
        onboardingFormReq,
      );

      setProfile({ ...organizationProfile });
    } catch (error) {
      setPage(page);
      setAlert(
        error?.message ||
          "Unable to save your information. Please reload and try again",
        "error",
      );
    }
  };

  useEffect(() => {
    if (profile?.id) {
      const copy = makeDeepCopy(profile);
      copy.name = currentUser?.name;
      setOnboardingForm(copy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  useEffect(() => {
    slideInLeft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const animateNext = async (currentPage: Page) => {
    await slideOutLeft();
    if (currentPage === Page.about) {
      setPage(Page.categories);
    } else if (currentPage === Page.categories) {
      if (
        (isPage(routes.recommendations) || isPage(routes.campaignView)) &&
        noTemplates
      ) {
        setProfileDialogOpen(false);
        setCreateTemplateOnboardingDialogOpen(true);
      } else {
        setPage(Page.complete);
      }
    }
    slideInLeft();
  };

  const handleNext = async (updatedForm?: Map) => {
    if (updatedForm) {
      setOnboardingForm(updatedForm);
    }

    const currentPage = page;
    if (currentPage === Page.complete) {
      setProfileDialogOpen(false);
      return;
    }
    await animateNext(currentPage);

    trackEvent("Complete Profile Dialog - Next Button Pressed", {
      page: currentPage,
      route: location.pathname,
    });

    if (currentPage === Page.about) {
      saveOnboardingForm(updatedForm || onboardingForm);
    }
  };

  const handleBack = async () => {
    trackEvent("Complete Profile Dialog - Back Button Pressed", {
      page,
      route: location.pathname,
    });

    await slideOutRight();
    if (page === Page.about) {
      setProfileDialogOpen(false);
    } else if (page === Page.categories) {
      setPage(Page.about);
    } else {
      setPage(Page.categories);
    }
    slideInRight();
  };

  const handleClose = () => {
    trackEvent("Complete Profile Dialog Closed", {
      page,
      route: location.pathname,
    });
    setProfileDialogOpen(false);
  };

  return (
    <Dialog
      maxWidth="lg"
      fullWidth
      scroll="body"
      fullScreen={isMobileScreen}
      open={true}
      onClose={handleClose}
    >
      <IconButton sx={styles.closeIcon} onClick={handleClose}>
        <Box component="i" className="fa-regular fa-xmark" />
      </IconButton>
      <DialogContent sx={styles.noOverflow}>
        <Grid container sx={[styles.noOverflow, styles.fullWidth]}>
          <Slide
            in={slideProps.in}
            direction={slideProps.direction}
            easing={{
              enter: theme.transitions.easing.easeInOut,
              exit: theme.transitions.easing.easeIn,
            }}
            timeout={{
              enter: ENTER_DELAY,
              exit: EXIT_DELAY,
            }}
          >
            <Grid sx={[styles.formContainer, styles.fullWidth]} item xs={12}>
              {page === Page.about && (
                <AboutForm
                  onboardingForm={onboardingForm}
                  setOnboardingForm={setOnboardingForm}
                  handleNext={handleNext}
                  hideDiscoveryQuestion
                  handleBack={handleBack}
                />
              )}
              {page === Page.categories && (
                <CategoryForm
                  onboardingForm={onboardingForm}
                  handleBack={handleBack}
                  moveNext={handleNext}
                />
              )}
              {page === Page.complete && (
                <Box>
                  <Typography
                    variant="h4"
                    paragraph
                    sx={{ textAlign: "center" }}
                  >
                    <Box
                      component="i"
                      color="primary.main"
                      sx={{ pr: 2 }}
                      className="fa-solid fa-party-horn"
                    />
                    Profile Completed!
                  </Typography>
                  <Typography sx={{ textAlign: "center" }}>
                    Congrats on filling out your profile! This helps us tailor
                    brand recommendations to you more effectively. Want even
                    more personalized matches? You can further customize your
                    profile on your{" "}
                    <Link
                      sx={{ textDecoration: "none" }}
                      component={RouterLink}
                      to={`/${routes.profile}`}
                      onClick={() => {
                        trackEvent(
                          "Complete Profile Dialog - Open Profile Page",
                          {
                            page,
                            route: location.pathname,
                          },
                        );
                        setProfileDialogOpen(false);
                      }}
                    >
                      <strong>Detailed Profile</strong>
                    </Link>{" "}
                    page to improve your brand recommendations.
                  </Typography>
                  {isPage(routes.home) && noTemplates && (
                    <>
                      <Divider sx={{ my: 3 }} />
                      <Typography
                        paragraph
                        variant="h4"
                        sx={{ textAlign: "center" }}
                      >
                        Next Step: Create Template
                      </Typography>
                      <Typography paragraph sx={{ textAlign: "center" }}>
                        Before you can start pitching brands effectively, you'll
                        need to create an email template. This gives you a
                        ready-to-use message that makes reaching out to brands
                        faster and easier.
                      </Typography>
                      <Box display="flex" justifyContent="center">
                        <Button
                          disableElevation
                          sx={{ textAlign: "center" }}
                          variant="contained"
                          onClick={() => {
                            trackEvent(
                              "Complete Profile Dialog - Create Template Started",
                              {
                                page,
                                route: location.pathname,
                              },
                            );
                            setProfileDialogOpen(false);
                            setCreateTemplateOnboardingDialogOpen(true);
                          }}
                        >
                          Start
                        </Button>
                      </Box>
                    </>
                  )}
                  {isPage(routes.home) &&
                    templates.length !== 0 &&
                    !profile?.hasOnboarded && (
                      <>
                        <Divider sx={{ my: 3 }} />
                        <Typography
                          paragraph
                          variant="h4"
                          sx={{ textAlign: "center" }}
                        >
                          Next Step: Send Your First Pitch
                        </Typography>
                        <Typography paragraph sx={{ textAlign: "center" }}>
                          To help you pitch brands effectively, let's guide you
                          through sending your first brand pitch using Bento.
                        </Typography>
                        <Box display="flex" justifyContent="center">
                          <Button
                            disableElevation
                            sx={{ textAlign: "center" }}
                            variant="contained"
                            onClick={() => {
                              trackEvent(
                                "Complete Profile Dialog - Send First Email Started",
                                {
                                  page,
                                  route: location.pathname,
                                },
                              );
                              setProfileDialogOpen(false);
                              setEmailBrandDialogOpen(true);
                            }}
                          >
                            Start
                          </Button>
                        </Box>
                      </>
                    )}
                  {!isPage(routes.home) && (
                    <Box display="flex" justifyContent="center">
                      <Button
                        disableElevation
                        sx={{ textAlign: "center", mt: 4, mb: -3 }}
                        color="secondary"
                        onClick={handleClose}
                      >
                        Close
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </Grid>
          </Slide>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileDialog;
